import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { bus } from '../../App';
import { api } from '../../lib/api';
import type { ContextMessage } from '../../shared/types/context';
import { ToggleButton } from '../ToggleButton';
import { useSelectionStore } from '../../state/selectionStore';
import { ToolCallBadge } from '../ToolCallBadge';
import { parseMessageBlocks, type MessageBlock } from '../../lib/messageBlocks';

interface ChatMessage {
  key: string;
  messageId?: number;
  role: 'user' | 'assistant';
  blocks: MessageBlock[];
  timestamp: string;
  isStreaming?: boolean;
  isNew?: boolean;
}

function toChatMessage(message: ContextMessage, keyOverride?: string): ChatMessage | null {
  if (message.role !== 'user' && message.role !== 'assistant') {
    return null;
  }

  const blocks = parseMessageBlocks(message.content);
  if (blocks.length === 0) {
    return null;
  }

  return {
    key: keyOverride ?? `msg-${message.id}`,
    messageId: message.id,
    role: message.role,
    blocks,
    timestamp: new Date(message.timestamp).toLocaleTimeString(),
  };
}

function extractSentencesFromText(text: string, forceRemainder = false) {
  let working = text ?? '';
  const sentences: string[] = [];

  while (working.length > 0) {
    const endIndex = working.search(/[.!?]/);
    if (endIndex === -1) {
      break;
    }

    let cursor = endIndex + 1;

    while (cursor < working.length && /[.!?]/.test(working[cursor])) {
      cursor += 1;
    }

    while (cursor < working.length && /[\)\]"'»]/.test(working[cursor])) {
      cursor += 1;
    }

    const candidate = working.slice(0, cursor).trim();
    if (candidate) {
      sentences.push(candidate);
    }

    working = working.slice(cursor).replace(/^\s+/, '');
  }

  if (forceRemainder) {
    const leftover = working.trim();
    if (leftover) {
      sentences.push(leftover);
      working = '';
    }
  }

  return { sentences, remainder: working };
}

function CopyableChatMessageContent({ content }: { content: string }) {
  const [copied, setCopied] = useState(false);
  const resetTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimeoutRef.current) {
        window.clearTimeout(resetTimeoutRef.current);
        resetTimeoutRef.current = null;
      }
    };
  }, []);

  const handleCopy = useCallback(async () => {
    if (!content) return;

    if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
      console.warn('[ChatPanel] Clipboard API not available');
      return;
    }

    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      if (resetTimeoutRef.current) {
        window.clearTimeout(resetTimeoutRef.current);
      }
      resetTimeoutRef.current = window.setTimeout(() => {
        setCopied(false);
        resetTimeoutRef.current = null;
      }, 2000);
    } catch (error) {
      console.error('[ChatPanel] Failed to copy message text:', error);
    }
  }, [content]);

  return (
    <div className="chat-message-content">
      <div className="chat-message-copy-controls panel-controls">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleCopy}
          aria-label="Copy message"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="chat-message-text">
        {content}
      </div>
    </div>
  );
}

function ChatMessageBubble({ message, toolCallResults }: {
  message: ChatMessage;
  toolCallResults: Map<string, string>;
}) {
  // Check if message has any text content
  const hasText = message.blocks.some(block => block.type === 'text');

  // If no text, render as a single line of badges (skip tool_result blocks)
  if (!hasText) {
    return (
      <div className="chat-message-badges-line">
        {message.blocks
          .filter(block => block.type === 'tool_call')
          .map((block, index) => (
            <ToolCallBadge
              key={index}
              toolName={block.toolName}
              content={block.content}
              resultContent={
                message.role === 'assistant' && block.toolId
                  ? toolCallResults.get(block.toolId)
                  : undefined
              }
            />
          ))}
      </div>
    );
  }

  // Group consecutive text blocks together
  const groupedBlocks: Array<{ type: 'text' | 'tool_calls'; content: string; toolCalls?: MessageBlock[] }> = [];

  for (const block of message.blocks) {
    if (block.type === 'text') {
      const lastGroup = groupedBlocks[groupedBlocks.length - 1];
      if (lastGroup && lastGroup.type === 'text') {
        lastGroup.content += block.content;
      } else {
        groupedBlocks.push({ type: 'text', content: block.content });
      }
    } else if (block.type === 'tool_call') {
      const lastGroup = groupedBlocks[groupedBlocks.length - 1];
      if (lastGroup && lastGroup.type === 'tool_calls') {
        lastGroup.toolCalls!.push(block);
      } else {
        groupedBlocks.push({ type: 'tool_calls', content: '', toolCalls: [block] });
      }
    }
    // Skip tool_result blocks - they don't render
  }

  return (
    <div className={`chat-message chat-message--${message.role}`}>
      {groupedBlocks.map((group, groupIndex) => {
        if (group.type === 'text') {
          return (
            <CopyableChatMessageContent
              key={groupIndex}
              content={group.content}
            />
          );
        } else {
          return (
            <div key={groupIndex} className="chat-message-badges-line">
              {group.toolCalls!.map((block, blockIndex) => (
                <ToolCallBadge
                  key={blockIndex}
                  toolName={block.toolName}
                  content={block.content}
                  resultContent={
                    message.role === 'assistant' && block.toolId
                      ? toolCallResults.get(block.toolId)
                      : undefined
                  }
                />
              ))}
            </div>
          );
        }
      })}
      <div className="chat-message-timestamp">{message.timestamp}</div>
    </div>
  );
}

export function ChatPanel() {
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [paused, setPaused] = useState(false);
  const speech_enabled = useSelectionStore((state) => state.speech_enabled);
  const setSpeechEnabled = useSelectionStore((state) => state.set);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const activeSessionIdRef = useRef<number | null>(null);
  const audioQueueRef = useRef<Array<() => Promise<void>>>([]);
  const isPlayingRef = useRef(false);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const speechEnabledRef = useRef(speech_enabled);
  const sentenceBufferRef = useRef('');
  const pendingSentencesRef = useRef<string[]>([]);
  const isGeneratingTtsRef = useRef(false);
  const currentStreamingKeyRef = useRef<string | null>(null);
  const streamingHandledRef = useRef(false);
  const speechHandledMessageIdsRef = useRef(new Set<number>());

  const consumeSentenceBuffer = useCallback((force = false) => {
    const { sentences, remainder } = extractSentencesFromText(sentenceBufferRef.current, force);
    sentenceBufferRef.current = remainder;
    return sentences;
  }, []);

  const stopAndClearAudio = useCallback(() => {
    // Stop currently playing audio
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }

    // Clear queue
    audioQueueRef.current = [];
    isPlayingRef.current = false;
  }, []);

  const processAudioQueue = useCallback(async () => {
    if (isPlayingRef.current || audioQueueRef.current.length === 0) {
      return;
    }

    isPlayingRef.current = true;

    while (audioQueueRef.current.length > 0) {
      const playNext = audioQueueRef.current.shift();
      if (playNext) {
        try {
          await playNext(currentAudioRef);
        } catch (error) {
          console.error('[ChatPanel] Failed to play queued audio:', error);
        }
      }
    }

    isPlayingRef.current = false;
  }, []);

  const queueAudio = useCallback((playFn: (audioRef: React.MutableRefObject<HTMLAudioElement | null>) => Promise<void>) => {
    audioQueueRef.current.push(playFn);
    void processAudioQueue();
  }, [processAudioQueue]);

  const processSpeechQueue = useCallback(async () => {
    if (!speechEnabledRef.current) {
      pendingSentencesRef.current = [];
      sentenceBufferRef.current = '';
      return;
    }
    if (isGeneratingTtsRef.current) return;
    if (pendingSentencesRef.current.length === 0) return;
    if (audioQueueRef.current.length > 0) return;

    const sentence = pendingSentencesRef.current.shift();
    if (!sentence) return;

    isGeneratingTtsRef.current = true;

    try {
      const data = await api().speak(sentence);
      if (!speechEnabledRef.current) {
        return;
      }

      const playAudio = async (audioRef: React.MutableRefObject<HTMLAudioElement | null>) => {
        const blob = new Blob([data], { type: 'audio/mpeg' });
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audioRef.current = audio;

        return new Promise<void>((resolve, reject) => {
          let settled = false;
          const cleanup = () => {
            if (audioRef.current === audio) {
              audioRef.current = null;
            }
            URL.revokeObjectURL(url);
          };
          const scheduleNext = () => {
            if (!speechEnabledRef.current) {
              pendingSentencesRef.current = [];
              sentenceBufferRef.current = '';
              return;
            }
            Promise.resolve().then(() => {
              void processSpeechQueue();
            });
          };
          const finalize = (handler: () => void) => {
            if (settled) return;
            settled = true;
            handler();
            cleanup();
            scheduleNext();
          };

          audio.onended = () => {
            finalize(resolve);
          };
          audio.onerror = () => {
            finalize(() => reject(new Error('Audio playback failed')));
          };
          audio.onpause = () => {
            if (speechEnabledRef.current) return;
            finalize(resolve);
          };
          audio.play().catch((error) => {
            finalize(() => reject(error));
          });
        });
      };

      queueAudio(playAudio);
    } catch (error) {
      console.error('[ChatPanel] Failed to synthesize speech:', error);
    } finally {
      isGeneratingTtsRef.current = false;
      if (!speechEnabledRef.current) {
        pendingSentencesRef.current = [];
        sentenceBufferRef.current = '';
        return;
      }
      if (pendingSentencesRef.current.length > 0) {
        Promise.resolve().then(() => {
          void processSpeechQueue();
        });
      }
    }
  }, [queueAudio]);

  const scheduleProcessSpeech = useCallback(() => {
    Promise.resolve().then(() => {
      void processSpeechQueue();
    });
  }, [processSpeechQueue]);

  const enqueueSentenceForSpeech = useCallback((sentence: string) => {
    const trimmed = sentence.trim();
    if (!trimmed || !speechEnabledRef.current) {
      return;
    }

    pendingSentencesRef.current.push(trimmed);
    scheduleProcessSpeech();
  }, [scheduleProcessSpeech]);

  const handleStreamingTextForSpeech = useCallback((delta: string) => {
    if (!speechEnabledRef.current || !delta) {
      return;
    }

    sentenceBufferRef.current += delta;

    const sentences = consumeSentenceBuffer(false);
    if (sentences.length > 0) {
      streamingHandledRef.current = true;
      sentences.forEach(enqueueSentenceForSpeech);
      return;
    }

    if (delta.trim().length > 0) {
      streamingHandledRef.current = true;
    }
  }, [consumeSentenceBuffer, enqueueSentenceForSpeech]);

  const flushSpeechBuffer = useCallback(() => {
    if (!speechEnabledRef.current) {
      sentenceBufferRef.current = '';
      return;
    }

    const sentences = consumeSentenceBuffer(true);
    if (!sentences.length) {
      return;
    }

    streamingHandledRef.current = true;
    sentences.forEach(enqueueSentenceForSpeech);
  }, [consumeSentenceBuffer, enqueueSentenceForSpeech]);

  const enqueueFullAssistantMessageForSpeech = useCallback((message: ChatMessage) => {
    if (!speechEnabledRef.current) {
      return;
    }

    const textContent = message.blocks
      .filter(block => block.type === 'text')
      .map(block => block.content)
      .join(' ')
      .trim();

    if (!textContent) {
      return;
    }

    const { sentences } = extractSentencesFromText(textContent, true);
    if (!sentences.length) {
      return;
    }

    sentences.forEach(enqueueSentenceForSpeech);
  }, [enqueueSentenceForSpeech]);

  const handleSpeechToggle = useCallback((enabled: boolean) => {
    setSpeechEnabled({ speech_enabled: enabled });
    speechEnabledRef.current = enabled;
    if (!enabled) {
      stopAndClearAudio();
      sentenceBufferRef.current = '';
      pendingSentencesRef.current = [];
      isGeneratingTtsRef.current = false;
      currentStreamingKeyRef.current = null;
      streamingHandledRef.current = false;
    } else {
      scheduleProcessSpeech();
    }
  }, [scheduleProcessSpeech, setSpeechEnabled, stopAndClearAudio]);

  useEffect(() => {
    speechEnabledRef.current = speech_enabled;
    if (!speech_enabled) {
      sentenceBufferRef.current = '';
      pendingSentencesRef.current = [];
      isGeneratingTtsRef.current = false;
      return;
    }

    scheduleProcessSpeech();
  }, [scheduleProcessSpeech, speech_enabled]);

  const toolCallResults = useMemo(() => {
    const map = new Map<string, string>();
    for (let index = 0; index < chatMessages.length - 1; index += 1) {
      const message = chatMessages[index];
      if (message.role !== 'assistant') continue;
      const nextMessage = chatMessages[index + 1];
      if (!nextMessage) continue;
      for (const block of message.blocks) {
        if (block.type !== 'tool_call' || !block.toolId) continue;
        const matchingBlocks = nextMessage.blocks.filter(
          (candidate) =>
            candidate.type === 'tool_result' && candidate.toolId === block.toolId,
        );
        if (!matchingBlocks.length) continue;
        const combined = matchingBlocks
          .map((match) => match.content)
          .filter(Boolean)
          .join('\n\n');
        if (!combined.trim()) continue;
        map.set(block.toolId, combined);
      }
    }
    return map;
  }, [chatMessages]);

  // Helper function to load chat history
  const loadChatHistory = useCallback(async (messageId: number) => {
    try {
      const message = await api().services.context.getMessage(messageId);
      if (!message || !message.session_id) {
        return;
      }

      const messages = await api().services.context.getMessages(message.session_id, messageId);
      if (!messages || !Array.isArray(messages)) {
        return;
      }

      activeSessionIdRef.current = message.session_id;

      const mapped = messages
        .map((msg: ContextMessage) => toChatMessage(msg))
        .filter((msg): msg is ChatMessage => msg !== null);

      setChatMessages(mapped);
    } catch (error) {
      console.error('[chat] failed to load history:', error);
    }
  }, []);

  const clearChat = useCallback(() => {
    activeSessionIdRef.current = null;
    speechHandledMessageIdsRef.current.clear();
    sentenceBufferRef.current = '';
    pendingSentencesRef.current = [];
    currentStreamingKeyRef.current = null;
    streamingHandledRef.current = false;
    setChatMessages([]);
  }, []);

  const appendAssistantDelta = useCallback(
    (payload: unknown, blockType: 'text' | 'tool_call') => {
      let content = '';
      let toolName: string | undefined;
      let toolId: string | undefined;

      if (blockType === 'tool_call' && payload && typeof payload === 'object') {
        const data = payload as any;
        toolName = data.name;
        toolId = data.id;

        // Only create/update a tool badge if we have a name
        if (!toolName) {
          return;
        }

        content = JSON.stringify(data.input ?? {}, null, 2);
      } else if (typeof payload === 'string') {
        content = payload;
      } else if (blockType === 'text') {
        content = JSON.stringify(payload ?? '', null, 2);
      } else {
        content = JSON.stringify(payload ?? {}, null, 2);
      }

      if (!content && blockType !== 'tool_call') {
        return;
      }

      let createdStreamingKey: string | null = null;

      setChatMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];

        if (!last || last.role !== 'assistant' || !last.isStreaming) {
          const streamingKey = `stream-${Date.now()}`;
          const streamingMessage: ChatMessage = {
            key: streamingKey,
            role: 'assistant',
            blocks: [{ type: blockType, content, toolName, toolId }],
            timestamp: new Date().toLocaleTimeString(),
            isStreaming: true,
          };
          createdStreamingKey = streamingKey;
          next.push(streamingMessage);
          return next;
        }

        const blocks = [...last.blocks];

        // For tool calls, find existing block by toolId or create new one
        if (blockType === 'tool_call' && toolId) {
          const existingIndex = blocks.findIndex(
            b => b.type === 'tool_call' && b.toolId === toolId
          );

          if (existingIndex !== -1) {
            // Update existing tool block
            blocks[existingIndex] = {
              ...blocks[existingIndex],
              content,
              toolName: toolName || blocks[existingIndex].toolName,
            };
          } else {
            // Add new tool block
            blocks.push({ type: blockType, content, toolName, toolId });
          }
        } else {
          // For text blocks, append to trailing block if same type
          const trailingBlock = blocks[blocks.length - 1];

          if (trailingBlock && trailingBlock.type === blockType) {
            blocks[blocks.length - 1] = {
              ...trailingBlock,
              content: trailingBlock.content + content,
            };
          } else {
            blocks.push({ type: blockType, content, toolName, toolId });
          }
        }

        next[next.length - 1] = { ...last, blocks };
        return next;
      });

      if (createdStreamingKey) {
        currentStreamingKeyRef.current = createdStreamingKey;
        streamingHandledRef.current = false;
        sentenceBufferRef.current = '';
      }

      if (blockType === 'text') {
        handleStreamingTextForSpeech(content);
      }
    },
    [handleStreamingTextForSpeech],
  );

  const promoteStreamingMessage = useCallback(
    (message: ChatMessage, isNew = false) => {
      let replacedStreamingKey: string | null = null;

      setChatMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];

        if (last && last.role === 'assistant' && last.isStreaming) {
          replacedStreamingKey = last.key;
          next[next.length - 1] = {
            ...message,
            key: last.key,
            isNew,
          };
          return next;
        }

        const existingIndex = next.findIndex((item) => item.messageId === message.messageId);
        if (existingIndex !== -1) {
          next[existingIndex] = {
            ...message,
            key: next[existingIndex].key,
            isNew,
          };
          return next;
        }

        next.push({ ...message, isNew });
        return next;
      });

      if (message.role !== 'assistant') {
        return;
      }

      if (replacedStreamingKey && currentStreamingKeyRef.current === replacedStreamingKey) {
        if (typeof message.messageId === 'number') {
          speechHandledMessageIdsRef.current.add(message.messageId);
        }
        currentStreamingKeyRef.current = null;
        streamingHandledRef.current = false;
        return;
      }

      if (!isNew || typeof message.messageId !== 'number') {
        return;
      }

      if (speechHandledMessageIdsRef.current.has(message.messageId)) {
        return;
      }

      if (!streamingHandledRef.current) {
        enqueueFullAssistantMessageForSpeech(message);
      }

      speechHandledMessageIdsRef.current.add(message.messageId);
    },
    [enqueueFullAssistantMessageForSpeech],
  );

  // Load chat history on mount
  useEffect(() => {
    const loadInitialHistory = async () => {
      try {
        const agentId = await api().services.meta.get('active_agent_id')
        const agent = await api().services.agent.get(agentId)
        const lastMessageId = agent?.last_message_id
        if (lastMessageId && typeof lastMessageId === 'number') {
          await loadChatHistory(lastMessageId);
        }
      } catch (error) {
        console.error('[chat] failed to load initial history:', error);
      }
    };
    loadInitialHistory();
  }, [loadChatHistory]);

  // Subscribe to bus events
  useEffect(() => {
    const unsubscribers: (() => void)[] = [];

    unsubscribers.push(bus.on('agent/update', async (_event, payload) => {
      const keys = new Set(Object.keys(payload))
      if (keys.has('last_message_id')) {
        const lastMessageId = payload.last_message_id
        if(lastMessageId){
          await loadChatHistory(lastMessageId);
        } else {
          clearChat();
        }
      }
    }));

    // Handle message/created to append new messages in the active session
    unsubscribers.push(
      bus.on('message/created', async (_event, payload) => {
        const sessionId = payload?.session_id;
        const messageId = payload?.message_id;
        if (typeof sessionId !== 'number' || typeof messageId !== 'number') {
          return;
        }

        if (activeSessionIdRef.current !== null && activeSessionIdRef.current !== sessionId) {
          return;
        }

        try {
          const created = await api().services.context.getMessage(messageId);
          if (!created) {
            return;
          }

          const mapped = toChatMessage(created);
          if (!mapped) {
            return;
          }

          activeSessionIdRef.current = sessionId;
          promoteStreamingMessage(mapped, true);
        } catch (error) {
          console.error('[chat] failed to handle message/created:', error);
        }
      }),
    );

    // Handle llm/start - create new assistant message
    unsubscribers.push(bus.on('llm/start', () => {
      const streamingKey = `stream-${Date.now()}`;
      let inserted = false;

      setChatMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last && last.role === 'assistant' && last.isStreaming) {
          return prev;
        }
        inserted = true;
        const streamingMessage: ChatMessage = {
          key: streamingKey,
          role: 'assistant',
          blocks: [],
          timestamp: new Date().toLocaleTimeString(),
          isStreaming: true,
        };
        return [...prev, streamingMessage];
      });

      if (inserted) {
        currentStreamingKeyRef.current = streamingKey;
        streamingHandledRef.current = false;
        sentenceBufferRef.current = '';
      }
    }));

    // Handle llm/delta/text - append text to last assistant message
    unsubscribers.push(bus.on('llm/delta/text', (_event, payload) => {
      appendAssistantDelta(payload, 'text');
    }));

    // Handle llm/delta/json - append JSON to last assistant message
    unsubscribers.push(bus.on('llm/delta/json', (_event, payload) => {
      appendAssistantDelta(payload, 'tool_call');
    }));

    const finalizeStreaming = () => {
      setChatMessages((prev) => {
        if (!prev.length) return prev;
        const last = prev[prev.length - 1];
        if (!last || !last.isStreaming) return prev;
        const next = [...prev];
        next[next.length - 1] = { ...last, isStreaming: false };
        return next;
      });
      flushSpeechBuffer();
    };

    unsubscribers.push(bus.on('llm/end', finalizeStreaming));
    unsubscribers.push(bus.on('llm/error', finalizeStreaming));
    unsubscribers.push(bus.on('llm/cancel', finalizeStreaming));
    unsubscribers.push(bus.on('llm/paused', finalizeStreaming));

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [appendAssistantDelta, clearChat, flushSpeechBuffer, loadChatHistory, promoteStreamingMessage]);

  useEffect(() => {
    return () => {
      clearChat();
    };
  }, [clearChat]);

  // Auto-scroll to bottom when new messages arrive (unless paused)
  useEffect(() => {
    if (paused) return;
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
    }
  }, [chatMessages, paused]);

  // Group consecutive tool-call-only messages together
  const groupedMessages: Array<ChatMessage | ChatMessage[]> = [];

  for (const message of chatMessages) {
    const hasOnlyToolCalls = message.blocks.every(block => block.type === 'tool_call' || block.type === 'tool_result');
    const hasVisibleContent = message.blocks.some(block => block.type === 'text' || block.type === 'tool_call');

    if (!hasVisibleContent) {
      // Skip messages with only tool_result blocks
      continue;
    }

    if (hasOnlyToolCalls) {
      const lastGroup = groupedMessages[groupedMessages.length - 1];
      if (Array.isArray(lastGroup)) {
        lastGroup.push(message);
      } else {
        groupedMessages.push([message]);
      }
    } else {
      groupedMessages.push(message);
    }
  }

  return (
    <div className="panel-shell chat-panel">
      <div className="panel-controls">
        <ToggleButton
          label="Pause"
          enabled={paused}
          onToggle={setPaused}
        />
        <ToggleButton
          label="Speech"
          enabled={speech_enabled}
          onToggle={handleSpeechToggle}
        />
      </div>
      <div className="chat-panel-wrapper">
        <div className="chat-messages-container">
          {chatMessages.length === 0 ? (
            <div className="empty-state">
              <p>No messages yet</p>
              <p className="empty-state-subtitle">Send a prompt to start a conversation</p>
            </div>
          ) : (
            <div className="chat-messages-list">
              {groupedMessages.map((item, index) => {
                if (Array.isArray(item)) {
                  // Render all tool calls from multiple messages in a single line
                  return (
                    <div key={`tool-group-${index}`} className="chat-message-badges-line">
                      {item.flatMap((message) =>
                        message.blocks
                          .filter(block => block.type === 'tool_call')
                          .map((block, blockIndex) => (
                            <ToolCallBadge
                              key={`${message.key}-${blockIndex}`}
                              toolName={block.toolName}
                              content={block.content}
                              resultContent={
                                message.role === 'assistant' && block.toolId
                                  ? toolCallResults.get(block.toolId)
                                  : undefined
                              }
                            />
                          ))
                      )}
                    </div>
                  );
                } else {
                  return (
                    <ChatMessageBubble
                      key={item.key}
                      message={item}
                      toolCallResults={toolCallResults}
                    />
                  );
                }
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
